import { AuthContext } from '@/context/AuthContext.tsx';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import ConductionAPI from '@/javascript/conduction-api.ts';
import { TimelineSectionDropCompletedEvent } from '@/javascript/custom-events.ts';
import { SongPartListCache } from '@/javascript/histomap.ts';
import { Song } from '@/javascript/library-objects.ts';
import MidiPlayer, { MidiPlayerTrack } from '@/javascript/midi-player.ts';
import { Instrument, Key, PartList, Section } from '@/javascript/music-objects.ts';
import { Utils } from '@/javascript/utils';
import {
	Badge,
	Box,
	Button,
	Card,
	Flex,
	Group,
	Highlight,
	Loader,
	MantineTheme,
	Paper,
	ScrollArea,
	Select,
	Stack,
	Text,
	TextInput,
	Tooltip
} from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import { IconAlertCircle, IconMusic, IconPlayerPlay, IconPlayerStop, IconX } from '@tabler/icons-react';
import React, {
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from 'react';
import { InstrumentName } from 'soundfont-player';
import type { DragPreviewInitPayload, DragPreviewSectionPayload } from './LibraryDragPreview.tsx';
import { LibraryPart } from './LibraryPart.tsx';

interface SongButtonProps {
	song: Song;
	isSelected: boolean;
	onClick: () => void;
	searchQuery: string;
	loadingSongId: string | null;
	isPhoneOrSmaller: boolean;
	isTabletOrSmaller: boolean;
}

const isNewSong = (uploadDate: string | Date, days = 30) => {
	const now = Date.now();
	const created = new Date(uploadDate).getTime();
	return now - created < days * 24 * 60 * 60 * 1000;
};

/**
 * Returns an array of nodes: [item, ", ", item, ", and ", item]
 * Supports 1, 2, 3+ with Oxford comma.
 */
function formatWithOxfordAnd<T>(
	items: T[],
	renderItem: (item: T, idx: number) => React.ReactNode
): React.ReactNode[] {
	const n = items.length;
	if (n === 0) return [];
	if (n === 1) return [renderItem(items[0], 0)];
	if (n === 2)
		return [
			renderItem(items[0], 0),
			<Text key="and" size="xs" span> and </Text>,
			renderItem(items[1], 1),
		];

	const nodes: React.ReactNode[] = [];
	items.forEach((item, idx) => {
		const isLast = idx === n - 1;
		const isSecondToLast = idx === n - 2;

		nodes.push(renderItem(item, idx));
		if (!isLast) {
			nodes.push(
				<Text key={`sep-${idx}`} size="xs" span>
					{isSecondToLast ? ", and " : ", "}
				</Text>
			);
		}
	});
	return nodes;
}

/* --------------------------------- hooks ----------------------------------- */

/**
 * Measures overflow and computes animation duration for marquee.
 * Recomputes on element resize and when `deps` change.
 */
function useMarquee(
	containerRef: React.RefObject<HTMLElement>,
	contentRef: React.RefObject<HTMLElement>,
	pixelsPerSecond = 50,
	deps: React.DependencyList = []
) {
	const [overflow, setOverflow] = React.useState(false);
	const [duration, setDuration] = React.useState(0);

	const recompute = React.useCallback(() => {
		const container = containerRef.current;
		const content = contentRef.current;
		if (!container || !content) return;

		const containerWidth = container.clientWidth;
		const contentWidth = (content as HTMLElement).scrollWidth;
		const isOverflowing = contentWidth > containerWidth;
		setOverflow(isOverflowing);

		if (isOverflowing) {
			const speed = Math.max(1, pixelsPerSecond);
			setDuration(contentWidth / speed);
		} else {
			setDuration(0);
		}
	}, [containerRef, contentRef, pixelsPerSecond]);

	React.useEffect(() => {
		recompute();
		const ro = new ResizeObserver(recompute);
		if (containerRef.current) ro.observe(containerRef.current);
		if (contentRef.current) ro.observe(contentRef.current);
		return () => ro.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [recompute, ...deps]);

	return { overflow, duration, recompute };
}

/* -------------------------------- component -------------------------------- */

export const SongButton: React.FC<SongButtonProps> = ({
	song,
	isSelected,
	onClick,
	searchQuery,
	loadingSongId,
	isPhoneOrSmaller
}) => {
	const [hovering, setHovering] = React.useState(false);

	// Title marquee refs
	const titleContainerRef = React.useRef<HTMLDivElement>(null);
	// In non-hover (ellipsis) mode, we attach ref to Text; in hover (marquee) mode, attach to the scrolling div.
	const titleContentRef = React.useRef<HTMLElement>(null as any);

	// Artists marquee refs
	const artistsContainerRef = React.useRef<HTMLDivElement>(null);
	const artistsContentRef = React.useRef<HTMLElement>(null as any);

	// Marquee computations (no need to depend on `hovering`)
	const { overflow: titleOverflow, duration: titleDuration } = useMarquee(
		titleContainerRef,
		titleContentRef,
		50,
		[song.songName]
	);

	const { overflow: artistsOverflow, duration: artistsDuration } = useMarquee(
		artistsContainerRef,
		artistsContentRef,
		50,
		[song.artists]
	);

	const composers = React.useMemo(
		() => song.artists.filter((a) => a.artistType.toLowerCase() === "composer"),
		[song.artists]
	);

	// Build inline nodes (no outer Group) so ellipsis can apply to the whole line
	const artistInlineNodes = React.useMemo(() => {
		return formatWithOxfordAnd(composers, (artist) => (
			<React.Fragment key={artist.artistId}>
				<Text size="xs" span>
					<Highlight component="span" highlight={searchQuery ? [searchQuery] : []}>
						{artist.artistName}
					</Highlight>
				</Text>
			</React.Fragment>
		));
	}, [composers, searchQuery]);

	const artistToGetLocationFrom = composers.find((artist) => artist.location !== undefined);
	const artistLocationBadge = artistToGetLocationFrom?.location && (
		<Badge
			opacity={0.65}
			size="xs"
			variant="gradient"
			gradient={{
				from: artistToGetLocationFrom.location.primaryColor || "blue",
				to: artistToGetLocationFrom.location.secondaryColor || "blue",
			}}
		>
			{artistToGetLocationFrom.location?.displayName} Artist
		</Badge>
	);

	const showNew = React.useMemo(() => isNewSong(song.uploadDate, 30), [song.uploadDate]);
	const newBadge = showNew && (
		<Badge
			opacity={0.65}
			color="yellow"
			size="xs"
		>
			New
		</Badge>
	);

	const songNameWithHighlight = (
		<Highlight fw={700} component="span" highlight={searchQuery ? [searchQuery] : []}>
			{song.songName}
		</Highlight>
	);

	const isLoadingThis = loadingSongId === song.songId;
	const disableOtherClicks = loadingSongId !== null && !isLoadingThis;

	return (
		<>
			{/* Global keyframes + truncation helper + edge fade for marquee */}
			<style>
				{`
			@keyframes marquee-linear {
			0%   { transform: translateX(0); }
			100% { transform: translateX(-50%); }
			}

			/* One-line truncation */
			.truncate-1line {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			}

			/* Edge fade mask for marquee */
			.marquee-mask {
			--fade: 5px;
			mask-image: linear-gradient(to right, transparent 0, black var(--fade), black calc(100% - var(--fade)), transparent 100%);
			-webkit-mask-image: linear-gradient(to right, transparent 0, black var(--fade), black calc(100% - var(--fade)), transparent 100%);
			}
        `}
			</style>

			<Button
				variant={isSelected ? "filled" : "outline"}
				onClick={disableOtherClicks ? undefined : onClick}
				disabled={disableOtherClicks}
				onMouseEnter={() => setHovering(true)}
				onMouseLeave={() => setHovering(false)}
				size="md"
				loading={isLoadingThis}
				aria-label={`Select song ${song.songName}`}
				styles={{ label: { width: "100%" } }}
				style={{
					width: "100%",
					transform: !isSelected ? (hovering ? "scale(0.95)" : "scale(0.9)") : "scale(1)",
					transition: "transform 0.3s ease",
					paddingLeft: 10,
					paddingRight: 10,
					position: "relative",
					overflow: "hidden",
				}}
				className='allow-vertical-scroll'
			>
				<Stack align="left" justify="center" gap={0} style={{ width: "100%" }}>
					{!isSelected &&
						<Stack
							gap={1}
							align="end"
							style={{ position: "absolute", bottom: 4, right: 8, pointerEvents: "none" }}
						>
							{newBadge}
							{artistLocationBadge}
						</Stack>
					}

					{/* Title row */}
					<Box style={{ height: 20, width: "100%" }}>
						<div
							ref={titleContainerRef}
							className={hovering && titleOverflow ? "marquee-mask" : undefined}
							style={{ width: "100%", minWidth: 0 }}
						>
							{/* Static (ellipsis) when not hovering or no overflow */}
							{(!hovering || !titleOverflow || titleDuration <= 0) ? (
								<Text
									size={isPhoneOrSmaller ? "xs" : "sm"}
									truncate
									ref={titleContentRef as React.RefObject<HTMLParagraphElement>}
								>
									{songNameWithHighlight}
								</Text>
							) : (
								<div
									// In marquee mode the scrolling div is what we measure
									ref={titleContentRef as unknown as React.RefObject<HTMLDivElement>}
									style={{
										display: "inline-flex",
										gap: "2rem",
										animation: `marquee-linear ${titleDuration}s linear infinite`,
									}}
								>
									<Text size={isPhoneOrSmaller ? "xs" : "sm"} span style={{ whiteSpace: "nowrap" }}>
										{songNameWithHighlight}
									</Text>
									<Text size={isPhoneOrSmaller ? "xs" : "sm"} span style={{ whiteSpace: "nowrap" }}>
										{songNameWithHighlight}
									</Text>
								</div>
							)}
						</div>
					</Box>

					{/* Artists row */}
					<div
						ref={artistsContainerRef}
						className={hovering && artistsOverflow ? "marquee-mask" : undefined}
						style={{ width: "100%", minWidth: 0 }}
					>
						{/* Static (ellipsis) when not hovering or no overflow */}
						{(!hovering || !artistsOverflow || artistsDuration <= 0) ? (
							<span
								ref={artistsContentRef as React.RefObject<HTMLSpanElement>}
								className="truncate-1line"
								style={{ display: "inline-block", maxWidth: "100%", verticalAlign: "bottom" }}
							>
								{artistInlineNodes}
							</span>
						) : (
							<Group
								// In marquee mode this scrolling div is measured
								ref={artistsContentRef as React.RefObject<HTMLDivElement>}
								wrap="nowrap"
								style={{
									display: "inline-flex",
									animation: `marquee-linear ${artistsDuration}s linear infinite`,
								}}
							>
								{artistInlineNodes}
								{artistInlineNodes}
							</Group>
						)}
					</div>
				</Stack>
			</Button>
		</>
	);
};

interface SongLibraryProps {
	setDraggingSections: (sections: Section[] | null) => void;
	draggingSections: Section[] | null;
}

export interface LibraryDragHandle {
	prepareDragStart: (sectionId: string, pointerPosition?: { x: number; y: number } | null) => {
		draggingSections: Section[];
		previewPayload: DragPreviewInitPayload | null;
	} | null;
	endDrag: () => void;
}

// For Mantine styling (replaces Library.css)
const useStyles = createStyles((_: MantineTheme) => ({
	selects: {
		flex: 1,
		margin: '0 0.5rem',
	},

	// Mantine Paper with background image
	sectionsPaper: {
		height: '100%',
		width: '100%',
		backgroundColor: '#f0f0f0',
		backgroundImage: `url('../assets/images/conduction-logo.svg')`,
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'right top',
		backgroundSize: '330px',
		overflow: 'auto',
		position: 'relative',
	},

	sectionLabels: {
		display: 'flex',
		backgroundColor: '#ffffff',
		position: 'absolute',
		top: 0,
		zIndex: 2,
	},

	sectionLabel: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		userSelect: 'none',
		borderRight: '1px solid #ccc',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},

	partsContainer: {
		position: 'absolute',
		display: 'flex',
		width: '100%',
	},

	partRow: {
		position: 'relative',
		borderBottom: '2px solid #585858',
	},
}));

// Constants
const MAX_MEASURE_WIDTH = 50;
const DEFAULT_ZOOM = 0;
const ZOOM_INTERVALS = 5;
const ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD = 200;
const MIN_PART_HEIGHT = 30;
const MAX_PART_HEIGHT = 50;
const SECTION_LABEL_HEIGHT = 24;
const PART_LABEL_WIDTH = 73;

const Library = forwardRef<LibraryDragHandle, SongLibraryProps>((props, ref) => {
	const { classes } = useStyles();

	const auth = useContext(AuthContext);
	if (auth === undefined) {
		throw new Error("The library must be a child of AuthContext.");
	}
	const { user, token } = auth;
	if (user === null || token === null) {
		throw new Error("The library can only be used once the user is logged in.");
	}

	const deviceLayout = useDeviceLayout();

	// ----------------------
	// State variables
	// ----------------------
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [selectedGenre, setSelectedGenre] = useState<string>('');
	const [selectedInstrument, setSelectedInstrument] = useState<string>('');
	const [selectedSong, setSelectedSong] = useState<Song | null>(null);
	const [selectedSongPartList, setSelectedSongPartList] = useState<PartList | null | undefined>(undefined);
	const [selectedSectionsIds, setSelectedSectionsIds] = useState<string[]>([]);
	const [isPlaying, setIsPlaying] = useState<boolean>(false);
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const [minMeasureWidth, setMinMeasureWidth] = useState<number>(deviceLayout.isPhoneOrSmaller ? 16 : deviceLayout.isTabletOrSmaller ? 18 : 20);
	const [measureWidth, setMeasureWidth] = useState<number>(
		minMeasureWidth + (MAX_MEASURE_WIDTH - minMeasureWidth) * (DEFAULT_ZOOM / ZOOM_INTERVALS)
	);
	useEffect(() => {
		const newMinMeasureWidth = deviceLayout.isPhoneOrSmaller ? 16 : deviceLayout.isTabletOrSmaller ? 18 : 20;
		setMinMeasureWidth(newMinMeasureWidth);
	}, [deviceLayout.isPhoneOrSmaller, deviceLayout.isTabletOrSmaller]);
	useEffect(() => {
		setMeasureWidth(
			minMeasureWidth + (MAX_MEASURE_WIDTH - minMeasureWidth) * (DEFAULT_ZOOM / ZOOM_INTERVALS)
		);
	}, [minMeasureWidth]);
	const [partHeight, setPartHeight] = useState<number>(
		(DEFAULT_ZOOM / (ZOOM_INTERVALS / 2)) * (MAX_PART_HEIGHT - MIN_PART_HEIGHT) + MIN_PART_HEIGHT
	);
	const [playingSectionName, setPlayingSectionName] = useState<string | null>(null);
	const [loadingSongId, setLoadingSongId] = useState<string | null>(null);
	const [searchTermToLog, setSearchTermToLog] = useState<string | null>(null);

	const sectionToPartIndex = useMemo(() => {
		const map = new Map<string, number>();
		selectedSongPartList?.parts.forEach((part, index) => {
			part.sections.forEach((section) => {
				map.set(section.id, index);
			});
		});
		return map;
	}, [selectedSongPartList]);

	const buildDragPreviewPayload = useCallback((sections: Section[], pointerPosition?: { x: number; y: number }): DragPreviewInitPayload | null => {
		if (typeof document === 'undefined' || sections.length === 0) {
			return null;
		}

		const previewSections: DragPreviewSectionPayload[] = [];
		sections.forEach((section) => {
			const element = document.querySelector(`[data-id="${section.id}"]`) as HTMLElement | null;
			if (!element) {
				return;
			}
			const rect = element.getBoundingClientRect();
			previewSections.push({
				section,
				partIndex: sectionToPartIndex.get(section.id) ?? 0,
				rect: {
					left: rect.left,
					top: rect.top,
					width: rect.width,
					height: rect.height
				},
				dragToken: section.getDragToken?.() ?? undefined
			});
		});

		if (previewSections.length === 0) {
			return null;
		}

		const base = previewSections.reduce(
			(acc, curr) => ({
				x: Math.min(acc.x, curr.rect.left),
				y: Math.min(acc.y, curr.rect.top)
			}),
			{ x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY }
		);

		const normalizedOrigin = {
			x: Number.isFinite(base.x) ? base.x : previewSections[0].rect.left,
			y: Number.isFinite(base.y) ? base.y : previewSections[0].rect.top
		};

		const initialCursor = pointerPosition ?? {
			x: previewSections[0].rect.left + previewSections[0].rect.width / 2,
			y: previewSections[0].rect.top + previewSections[0].rect.height / 2
		};

		return {
			sections: previewSections,
			groupOrigin: normalizedOrigin,
			pointerOffset: {
				x: initialCursor.x - normalizedOrigin.x,
				y: initialCursor.y - normalizedOrigin.y
			},
			initialCursor
		};
	}, [sectionToPartIndex]);

	// ----------------------
	// Refs
	// ----------------------
	const librarySectionContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Clear sections on drop
		const clearSelectedSections = () => {
			setSelectedSectionsIds([]);
		};

		return () => {
			TimelineSectionDropCompletedEvent.unsubscribe(clearSelectedSections);
			MidiPlayer.stop();
		};
	}, []);

	const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		// Clean the search query (remove special characters, trim whitespace, convert to lowercase, and remove extra spaces)
		const cleanedSearchQuery = searchQuery.replace(/[^\w\s]/gi, '').trim().toLowerCase().replace(/\s+/g, ' ');
		const cleanedInput = event.currentTarget.value.replace(/[^\w\s]/gi, '').trim().toLowerCase().replace(/\s+/g, ' ');
		// Register the search term to log is null and the search query is getting shorter (user is deleting characters)
		// Or the search term to log is not null and the search query is getting longer (user is typing characters)
		if (cleanedSearchQuery.length > 3 && ((searchTermToLog === null && cleanedSearchQuery.length > cleanedInput.length) || (searchTermToLog !== null && cleanedInput.length >= searchTermToLog.length))) {
			setSearchTermToLog(cleanedSearchQuery);
		}
		// Perform to the log if the current value is empty and the search term to log is not null
		if (searchTermToLog !== null && event.currentTarget.value.length === 0) {
			// User must be logged in
			if (auth.user !== null && auth.token !== null && !auth.user.roles.includes('Guest')) {
				ConductionAPI.logSearchTerm(searchTermToLog, auth.token);
				setSearchTermToLog(null);
			}
		}
		setSearchQuery(event.currentTarget.value);
	}, [searchQuery, searchTermToLog, auth.user, auth.token]);

	const handleGenreSelect = useCallback((value: string | null) => {
		setSelectedGenre(value ?? '');
	}, []);

	const handleInstrumentSelect = useCallback((value: string | null) => {
		setSelectedInstrument(value ?? '');
	}, []);

	const handleClearFilters = useCallback(() => {
		const cleanedSearchQuery = searchQuery.replace(/[^\w\s]/gi, '').trim().toLowerCase().replace(/\s+/g, ' ');
		if ((cleanedSearchQuery.length > 3 || searchTermToLog !== null) && auth.user !== null && auth.token !== null && !auth.user.roles.includes('Guest')) {
			const finalSearchTerm = searchTermToLog !== null ? searchTermToLog : cleanedSearchQuery;
			ConductionAPI.logSearchTerm(finalSearchTerm, auth.token);
			setSearchTermToLog(null);
		}

		setSelectedGenre('');
		setSelectedInstrument('');
		setSearchQuery('');
	}, [searchQuery, searchTermToLog, auth.user, auth.token]);

	const handleSongClick = useCallback(async (song: Song) => {
		MidiPlayer.stop();

		if (selectedSong !== null && selectedSong.songId === song.songId) {
			setSelectedSong(null);
			setPlayingSectionName(null);
			setSelectedSectionsIds([]);
			setSelectedSongPartList(undefined);
			return;
		}

		setSelectedSong(song);
		setPlayingSectionName(null);
		setSelectedSectionsIds([]);
		setSelectedSongPartList(undefined);

		const selectedSongId = song.songId;
		if (selectedSongId) {
			// Try to get from cache
			const cached = SongPartListCache.get(selectedSongId);
			if (cached) {
				setSelectedSongPartList(cached);

				if (auth.beginnerTourActive && auth.currentTourStep === 1) {
					auth.moveToNextTourStep();
				}
			} else {
				setLoadingSongId(selectedSongId);
				const notificationId = Utils.displayNotification(
					`Loading stems for "${song.songName}"`,
					undefined,
					"blue",
					"loading"
				);
				try {
					const songPartList = await ConductionAPI.getSongData(song.songId);
					if (songPartList === null) {
						Utils.updateNotification(
							notificationId,
							'Song Load Failure',
							`Failed to load song stems for "${song.songName}". We are sorry. Please try again later.`,
							'red',
							<IconAlertCircle size={16} />,
							true,
							false
						);
						setSelectedSongPartList(null);
					} else {
						// Transpose song partlist to the key of C major / A minor (0 fifths)
						songPartList.transpose(new Key(0))
						Utils.updateNotification(
							notificationId,
							`Loaded stems for "${song.songName}"`,
							undefined,
							"green",
							<IconMusic size={16} />,
							true,
							1000
						);
						songPartList.setName(song.songName);
						SongPartListCache.set(selectedSongId, songPartList);
						setSelectedSongPartList(songPartList);

						if (auth.beginnerTourActive && auth.currentTourStep === 1) {
							auth.moveToNextTourStep();
						}
					}
				} catch (e) {
					console.error(e);
					Utils.displayNotification(
						'Song Load Failure',
						'Something went wrong when loading the song stems. Please try again later.',
						'red',
						<IconAlertCircle size={16} />,
						true,
						false
					);
					setSelectedSongPartList(null);
				} finally {
					setLoadingSongId(null);
				}
			}
		}
	}, [selectedSong, props]);

	const handleWhiteSpaceClick = useCallback(() => {
		MidiPlayer.stop();
		setSelectedSectionsIds([]);
		setPlayingSectionName(null);
	}, []);

	/** Toggles or adds a selected section. */
	const handleBoxSelect = useCallback(
		(sectionId: string) => {
			if (!selectedSongPartList || !selectedSong) return;

			const isSelected = selectedSectionsIds.includes(sectionId);

			if (isSelected) {
				const newIds = selectedSectionsIds.filter((id) => id !== sectionId);
				setSelectedSectionsIds(newIds);

				if (playingSectionName != null) {
					MidiPlayer.removeTrack(sectionId);
					if (MidiPlayer.getTrackCount() <= 0) {
						MidiPlayer.stop();
					}
				}
			} else {
				const newSelectedPart = selectedSongPartList.parts.find((p) =>
					p.sections.find((s) => s.id === sectionId)
				);
				if (!newSelectedPart) return;

				const newSelectedSection = newSelectedPart.sections.find((s) => s.id === sectionId);
				if (!newSelectedSection) return;

				const newSectionName = newSelectedSection.name;
				const isSameName = playingSectionName != null && playingSectionName === newSectionName;

				if (isSameName) {
					// This is the same section of the song that was playing previously. We will just add this section to the selection and continue playing the preview with it.
					const newIds = [...selectedSectionsIds, sectionId];
					setSelectedSectionsIds(newIds);
					if (newSelectedPart.instrument) {
						const newTrack: MidiPlayerTrack = {
							measures: newSelectedSection.toMidiMeasures(),
							instrumentName: newSelectedPart.instrument.soundfontInstrument as InstrumentName,
							trackId: newSelectedSection.id,
							isPercussion: newSelectedPart.instrument.midiProgram === 0,
						};
						MidiPlayer.addTrack(newTrack);
					}

					// Report the preview to the API for usage tracking
					ConductionAPI.reportPreview(selectedSong.songId, newSelectedPart.name, newSelectedSection.name, token);
				} else {
					// This is a different section of the song than previously selected. Remove the other selections and start playing the preview with this section.
					const newIds = [sectionId];
					setSelectedSectionsIds(newIds);
					const selectedSectionsMatchingName = selectedSongPartList.parts
						.flatMap((part) => part.sections)
						.filter((section) => newIds.includes(section.id))
						.filter(
							(section) => section.name === newSectionName || section.id === newSelectedSection.id
						);

					MidiPlayer.stop();
					setPlayingSectionName(newSectionName);
					playPreview(selectedSectionsMatchingName);
				}
			}
		},
		[selectedSong, selectedSongPartList, selectedSectionsIds, playingSectionName, token]
	);

	const handleFullSectionSelect = useCallback(
		(event: React.MouseEvent, sectionName: string) => {
			if (!selectedSongPartList) return;
			event.stopPropagation();

			const sectionsWithName = selectedSongPartList.parts.flatMap(p => p.sections).filter(s => s.name === sectionName && !s.isEmpty());
			const isSelected = sectionsWithName.every(s => selectedSectionsIds.includes(s.id));

			if (isSelected) {
				// Deselect the section
				MidiPlayer.stop();
				setSelectedSectionsIds([]);
				setPlayingSectionName(null);
			} else {
				const newIds = [...sectionsWithName.map(s => s.id)];
				setSelectedSectionsIds(newIds);

				const isSameName = playingSectionName != null && playingSectionName === sectionName;

				if (isSameName) {
					const sectionsToStartPlaying = sectionsWithName.filter(s => !selectedSectionsIds.includes(s.id))
					// This is the same section of the song that was playing previously. We will just add this section to the selection and continue playing the preview with it.
					for (const section of sectionsToStartPlaying) {
						const part = section.getPart();
						if (part !== null && part.instrument !== null) {
							const newTrack: MidiPlayerTrack = {
								measures: section.toMidiMeasures(),
								instrumentName: part.instrument.soundfontInstrument as InstrumentName,
								trackId: section.id,
								isPercussion: part.instrument.midiProgram === 0,
							};
							MidiPlayer.addTrack(newTrack);
						}
					}
				} else {
					// This is a different section of the song than previously selected. Start playing the preview with this section.
					MidiPlayer.stop();
					setPlayingSectionName(sectionName);
					playPreview(sectionsWithName);
				}
			}
		},
		[selectedSongPartList, selectedSectionsIds, playingSectionName]
	);

	const handlePlayButtonClick = useCallback((event: React.MouseEvent) => {
		if (!selectedSongPartList) return;
		event.stopPropagation();
		const selectedSectionsMatchingName = selectedSongPartList.parts
			.flatMap((part) => part.sections)
			.filter((section) => selectedSectionsIds.includes(section.id))
			.filter((section) => section.name === playingSectionName);
		playPreview(selectedSectionsMatchingName);
	}, [playingSectionName, selectedSectionsIds, selectedSongPartList]);

	const playPreview = useCallback((sectionsToPlay: Section[]) => {
		if (!sectionsToPlay || !selectedSong) return;

		const playLoopCallback = async () => { };
		const stopCallback = () => {
			setIsPlaying(false);
		};
		const removeTrackCallback = () => {
			if (MidiPlayer.getTrackCount() <= 0) {
				MidiPlayer.stop();
				setPlayingSectionName(null);
			}
		};

		MidiPlayer.setCallbacks(playLoopCallback, stopCallback, removeTrackCallback);

		let shouldContinue = true;
		Promise.all(
			sectionsToPlay.map(async (section) => {
				const part = section.getPart();
				if (!part) {
					console.error(`Part not found for section: ${section}`);
					return;
				}

				const instrument = part.instrument;
				if (!instrument) {
					console.error(`Part instrument not found for section: ${section}`);
					return;
				}

				// Report the preview to the API for usage tracking
				ConductionAPI.reportPreview(selectedSong.songId, part.name, section.name, token);

				const newTrack: MidiPlayerTrack = {
					measures: section.toMidiMeasures(),
					instrumentName: instrument.soundfontInstrument as InstrumentName,
					trackId: section.id,
					isPercussion: instrument.midiProgram === 0,
				};
				shouldContinue = shouldContinue && await MidiPlayer.addTrack(newTrack);
			})
		).then(() => {
			if (!shouldContinue) return;
			MidiPlayer.play();
			setIsPlaying(true);
		});
	}, [selectedSong, token]);

	const prepareDragStart = useCallback(
		(sectionId: string, pointerPosition?: { x: number; y: number } | null) => {
			if (!selectedSongPartList || !selectedSong) return null;

			if (auth.beginnerTourActive && auth.currentTourStep === 2) {
				auth.moveToNextTourStep();
			}

			let newSet = new Set(selectedSectionsIds);
			if (!newSet.has(sectionId)) {
				newSet = new Set([sectionId]);
			}

			MidiPlayer.stop();
			setIsDragging(true);
			const nextSelectedIds = [...newSet];
			setSelectedSectionsIds(nextSelectedIds);

			const draggingSections = selectedSongPartList.parts
				.flatMap((part) => part.sections)
				.filter((section) => newSet.has(section.id));

			const dragSessionId = Utils.generateGUID();
			draggingSections.forEach((section, index) => {
				section.setDragToken(`${dragSessionId}-${index}-${section.id}`);
			});

			for (const section of draggingSections) {
				const part = section.getPart();
				if (!part) {
					console.error(`Part not found for section: ${section}`);
					continue;
				}

				ConductionAPI.reportDrag(
					selectedSong.songId,
					section.getLength(),
					part.name,
					section.name,
					token
				);
			}

			const previewPayload = buildDragPreviewPayload(
				draggingSections,
				pointerPosition ?? undefined
			);

			return {
				draggingSections,
				previewPayload,
			};
		},
		[
			selectedSong,
			selectedSongPartList,
			selectedSectionsIds,
			token,
			auth.beginnerTourActive,
			auth.currentTourStep,
			auth.moveToNextTourStep,
			buildDragPreviewPayload
		]
	);

	const endDrag = useCallback(() => {
		if (auth.beginnerTourActive && auth.currentTourStep === 3) {
			auth.moveToPrevTourStep();
		}

		setIsDragging(false);
		props.setDraggingSections(null);
		setPlayingSectionName(null);
	}, [auth.beginnerTourActive, auth.currentTourStep, auth.moveToPrevTourStep, props]);

	useImperativeHandle(ref, () => ({
		prepareDragStart,
		endDrag,
	}), [prepareDragStart, endDrag]);

	const calculateCurrentZoom = useCallback((): number => {
		return (
			((measureWidth - minMeasureWidth) / (MAX_MEASURE_WIDTH - minMeasureWidth)) *
			ZOOM_INTERVALS
		);
	}, [measureWidth]);

	const updateZoom = useCallback(
		(newZoom: number, focalPointX?: number, focalPointY?: number) => {
			if (!selectedSongPartList || !librarySectionContainerRef.current) return;
			const libraryContainer = librarySectionContainerRef.current;
			const boundingRect = libraryContainer.getBoundingClientRect();
			const focalX =
				focalPointX !== undefined
					? focalPointX - boundingRect.left - PART_LABEL_WIDTH
					: boundingRect.width / 2 - PART_LABEL_WIDTH;
			const focalY =
				focalPointY !== undefined
					? focalPointY - boundingRect.top
					: boundingRect.height / 2;

			const xHighSnapPoint =
				boundingRect.width - PART_LABEL_WIDTH - ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD;
			const yHighSnapPoint =
				boundingRect.height - ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD;

			let focalPointXRelativeToLibrary =
				focalX < ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD
					? 0
					: focalX > xHighSnapPoint
						? boundingRect.width - PART_LABEL_WIDTH
						: focalX;
			let focalPointYRelativeToLibrary =
				focalY < ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD
					? 0
					: focalY > yHighSnapPoint
						? boundingRect.height
						: focalY;

			const currentZoom = calculateCurrentZoom();
			const zoomingOut = newZoom < currentZoom;
			newZoom = Math.round(newZoom);

			if (newZoom === currentZoom) {
				newZoom = zoomingOut ? newZoom - 1 : newZoom + 1;
			}
			newZoom = Math.max(0, Math.min(ZOOM_INTERVALS - 1, newZoom));

			const libraryWidth = measureWidth * selectedSongPartList.getLength();
			const focalPointXRatio =
				(libraryContainer.scrollLeft + focalPointXRelativeToLibrary) / libraryWidth;
			const totalHeight =
				partHeight * selectedSongPartList.parts.length + SECTION_LABEL_HEIGHT;
			const focalPointYRatio =
				(libraryContainer.scrollTop + focalPointYRelativeToLibrary) / totalHeight;

			const nextMeasureWidth =
				minMeasureWidth +
				(MAX_MEASURE_WIDTH - minMeasureWidth) * (newZoom / ZOOM_INTERVALS);

			const halfwayZoomInterval = ZOOM_INTERVALS / 2;
			const nextPartHeight =
				(newZoom / halfwayZoomInterval) * (MAX_PART_HEIGHT - MIN_PART_HEIGHT) +
				MIN_PART_HEIGHT;

			setMeasureWidth(nextMeasureWidth);
			setPartHeight(nextPartHeight);

			requestAnimationFrame(() => {
				const newLibraryWidth = nextMeasureWidth * selectedSongPartList.getLength();
				libraryContainer.scrollLeft =
					focalPointXRatio * newLibraryWidth - focalPointXRelativeToLibrary;

				const newTotalHeight =
					nextPartHeight * selectedSongPartList.parts.length + SECTION_LABEL_HEIGHT;
				libraryContainer.scrollTop = Math.min(
					focalPointYRatio * newTotalHeight - focalPointYRelativeToLibrary,
					libraryContainer.scrollHeight - libraryContainer.clientHeight
				);

				const libraryHeightMinusScroll = newTotalHeight - libraryContainer.scrollTop;
				if (zoomingOut && libraryHeightMinusScroll < libraryContainer.clientHeight) {
					libraryContainer.scrollTop -= libraryContainer.clientHeight - libraryHeightMinusScroll;
				}
			});
		},
		[
			selectedSongPartList,
			librarySectionContainerRef,
			measureWidth,
			partHeight,
			calculateCurrentZoom,
		]
	);

	const handleLibraryZoomInternal = (event: WheelEvent) => {
		if (isDragging) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if (event.ctrlKey) {
			event.preventDefault();
			event.stopPropagation();

			if (Math.abs(event.deltaY) > 1) {
				updateZoom(calculateCurrentZoom() - event.deltaY / 100, event.clientX, event.clientY);
			}
		}
	};

	useEffect(() => {
		const container = librarySectionContainerRef.current;
		// Add wheel scroll event listener with passive set to false
		// Normally, we want to add event listeners in the JSX, but we need to set passive to false to prevent the default behavior of the wheel event
		container?.addEventListener('wheel', handleLibraryZoomInternal, { passive: false });

		return () => {
			container?.removeEventListener('wheel', handleLibraryZoomInternal);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSongPartList, handleLibraryZoomInternal]);

	// ----------------------
	// Filter logic for songs
	// ----------------------
	const songsWithSelectedInstrument = useMemo(() => auth.songs?.filter(
		(song) => selectedInstrument === '' || song.instruments.map((i) => i.id).includes(selectedInstrument)
	) ?? [], [auth.songs, selectedInstrument]);

	const songsWithSelectedGenre = useMemo(() => auth.songs?.filter(
		(song) => selectedGenre === '' || song.genres.map((g) => g.genreId).includes(selectedGenre)
	) ?? [], [auth.songs, selectedGenre]);

	const uniqueGenres = useMemo(() => {
		return songsWithSelectedInstrument
			.flatMap((song) => song.genres)
			.filter(
				(genre, index, self) =>
					self.findIndex((searchGenre) => searchGenre.genreId === genre.genreId) === index
			);
	}, [songsWithSelectedInstrument]);

	const uniqueInstruments = songsWithSelectedGenre
		.flatMap((song) => song.instruments)
		.filter(
			(instrument, index, self) =>
				self.findIndex((searchInstrument) => searchInstrument.id === instrument.id) === index
		)
		.filter(
			(instrument) => {
				const allInstrumentOptions = Instrument.getAllInstrumentOptions();
				return allInstrumentOptions.some(lei => lei.id === instrument.id) === true;
			}
		);

	// Apply search filtering (searching song names, artist names, and genre names)
	const filteredSongs = useMemo(() => {
		// Start with songs filtered by instrument and genre
		const baseFiltered = songsWithSelectedInstrument.filter((song) =>
			songsWithSelectedGenre.includes(song)
		);
		if (!searchQuery.trim()) return baseFiltered;
		const lowerSearch = searchQuery.toLowerCase();
		return baseFiltered.filter((song) => {
			const songNameMatch = song.songName.toLowerCase().includes(lowerSearch);
			const artistMatch = song.artists.some(artist => artist.artistName.toLowerCase().includes(lowerSearch));
			const genreMatch = song.genres.some(genre => genre.genreName.toLowerCase().includes(lowerSearch));
			return songNameMatch || artistMatch || genreMatch;
		});
	}, [songsWithSelectedInstrument, songsWithSelectedGenre, searchQuery]);

	// ----------------------
	// Render
	// ----------------------
	const libraryWidth = deviceLayout.isTabletOrSmaller ? (deviceLayout.isPhoneOrSmaller ? '128px' : '180px') : '280px';
	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				backgroundColor: '#f0f0f0',
				overflow: 'hidden',
				display: 'flex',
				flexDirection: 'row'
			}}
		>
			{/* Sidebar for songs and filters */}
			<div
				className="beginner-tour-song-library" // Beginner tour
			>
				<div
					style={{
						width: libraryWidth,
						borderRight: '2px solid #585858',
						display: 'flex',
						flexDirection: 'column',
						height: '100%'
					}}
				>
					<Box>
						<Box
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'left',
								justifyContent: 'left',
								padding: '0.2rem',
							}}
						>
							<TextInput
								placeholder="Search Library..."
								value={searchQuery}
								onChange={handleSearchChange}
								size={"xs"}
								error={filteredSongs.length === 0}
								// Prevent the key strokes from propagating to the Timeline where they will be consumed
								onKeyDown={(event) => { event.stopPropagation(); }}
								onKeyUp={(event) => { event.stopPropagation(); }}
							/>
							{!deviceLayout.isTabletOrSmaller &&
								(
									<Box
										style={{
											display: 'flex',
											flexDirection: 'row',
											backgroundColor: '#ffffff',
											justifyContent: 'space-around',
											alignItems: 'center',
										}}
									>
										<Tooltip
											label='Filter Library by Genre'
											withArrow
										>
											<Select
												className={classes.selects}
												data={[
													{ value: '', label: 'All Genres' },
													...uniqueGenres.map((genre) => ({
														value: genre.genreId,
														label: genre.genreName,
													})),
												]}
												value={selectedGenre}
												onChange={handleGenreSelect}
												placeholder="Genre"
												clearable={false}
												size="xs"
												style={{
													marginRight: 2,
													maxWidth: 100
												}}
												styles={{
													input: {
														minHeight: 18,
														height: 10,
													}
												}}
											/>
										</Tooltip>

										<Tooltip
											label='Filter Library by Instrument'
											withArrow
										>
											<Select
												className={classes.selects}
												data={[
													{ value: '', label: 'Any Instrument' },
													...uniqueInstruments.map((inst) => ({
														value: inst.id,
														label: inst.name,
													})),
												]}
												value={selectedInstrument}
												onChange={handleInstrumentSelect}
												placeholder="Instrument"
												clearable={false}
												size="xs"
												style={{
													marginLeft: 0,
													marginRight: 0,
													minWidth: 120,
												}}
												styles={{
													input: {
														minHeight: 18,
														height: 18
													}
												}}
											/>
										</Tooltip>

										<Tooltip
											label='Clear Library Filters'
											withArrow
										>
											<Button
												variant="subtle"
												size="compact-xs"
												ml="xs"
												onClick={handleClearFilters}
												title="Clear all filters"
												disabled={selectedInstrument === "" && selectedGenre === "" && searchQuery === ""}
												style={{
													marginLeft: 0,
													padding: 0,
													visibility: selectedInstrument === "" && selectedGenre === "" && searchQuery === "" ? "hidden" : "visible"
												}}
											>
												<IconX size={12} />
											</Button>
										</Tooltip>
									</Box>
								)}
						</Box>
					</Box>

					<ScrollArea scrollbars="y">
						{auth.songs === null ? (
							<Group mt="lg" justify="center">
								<Loader variant="dots" />
							</Group>
						) : (
							<Stack
								align="center"
								justify="center"
								gap={0}
								style={{
									maxWidth: libraryWidth
								}}
							>
								{filteredSongs.length > 0 ? (
									filteredSongs.map((song) => {
										const isSelected = selectedSong != null && selectedSong.songId === song.songId;
										return (
											<SongButton
												key={song.songId}
												song={song}
												isSelected={isSelected}
												onClick={() => handleSongClick(song)}
												searchQuery={searchQuery}
												loadingSongId={loadingSongId}
												isPhoneOrSmaller={deviceLayout.isPhoneOrSmaller}
												isTabletOrSmaller={deviceLayout.isTabletOrSmaller}
											/>
										);
									})
								) : (
									<Stack
										key="no-songs"
										style={{
											width: '100%',
											textAlign: 'center',
											padding: '0.5rem',
											alignItems: 'center',
										}}
									>
										<Text size="sm">No songs found</Text>
										<Button
											title="Clear Filters"
											size="xs"
											onClick={handleClearFilters}
											style={{
												marginTop: 5,
												padding: 0,
												width: '100px'
											}}
										>
											Clear Filters
										</Button>
									</Stack>
								)}
							</Stack>
						)}
					</ScrollArea>
				</div>
			</div>

			{/* Section container (zoomable, scrollable, background image) */}
			<Box
				className={"beginner-tour-get-stems"}
				style={{
					flex: 1,
					position: 'relative',
					width: 1,
				}}
			>
				<Paper
					ref={librarySectionContainerRef}
					className={classes.sectionsPaper + " allow-vertical-scroll"}
					onClick={handleWhiteSpaceClick}
					style={{
						overflow: isDragging ? "hidden" : "auto",
					}}
				>
					{selectedSongPartList && (
						<>
							<Card
								style={{
									position: "fixed",
									width: PART_LABEL_WIDTH,
									height: SECTION_LABEL_HEIGHT,
									zIndex: 7,
									padding: 0
								}}
								color="white"
							>
								<Flex
									justify={"center"}
									align={"center"}
									style={{
										height: "100%",
										width: "100%",
										borderBottom: "1px solid black",
										borderRight: "1px solid black"
									}}
								>
									{isPlaying && selectedSectionsIds.length > 0 ? (
										<Tooltip label="Stop Stem Preview" position="top" withArrow>
											<Button
												style={{
													height: "80%",
													width: "80%"
												}}
												onClick={(e) => {
													e.stopPropagation();
													MidiPlayer.stop();
												}}
											>
												<IconPlayerStop size={18} />
											</Button>
										</Tooltip>
									) : (
										<Tooltip label={selectedSectionsIds.length === 0 ? "Preview Selected Stems (None Selected)" : "Preview Selected Stems"} position="top" withArrow>
											<Button
												style={{
													height: "80%",
													width: "80%"
												}}
												disabled={selectedSectionsIds.length === 0}
												onClick={handlePlayButtonClick}
												variant="outline"
											>
												<IconPlayerPlay size={18} />
											</Button>
										</Tooltip>
									)}
								</Flex>
							</Card>
							<div
								className={classes.sectionLabels + " allow-vertical-scroll"}
								style={{
									position: "sticky",
									width: selectedSongPartList.getLength() * measureWidth,
									left: PART_LABEL_WIDTH,
									height: SECTION_LABEL_HEIGHT,
									zIndex: 6,
									borderBottom: "1px solid black"
								}}
							>
								{selectedSongPartList.parts.length > 0 &&
									selectedSongPartList.parts[0]
										.getSectionInfo()
										.map(({ sectionName, sectionLength }) => (
											<Paper
												key={sectionName + '-label'}
												className={classes.sectionLabel}
												style={{
													width: sectionLength * measureWidth,
													cursor: "pointer"
												}}
												bg={playingSectionName === sectionName ? "darkGreen.3" : "white"}
												onClick={(e) => handleFullSectionSelect(e, sectionName)}
											>
												<Text
													size="xs"
													style={{
														fontSize: 11
													}}
													fw={playingSectionName === sectionName ? 700 : 500}
												>
													{sectionName}
												</Text>
											</Paper>
										))}
							</div>

							<div
								className={classes.partsContainer}
								style={{
									height:
										selectedSongPartList.parts.length * partHeight,
									width:
										selectedSongPartList.getLength() * measureWidth + PART_LABEL_WIDTH,
								}}
							>
								{selectedSongPartList.parts.length > 0 && (
									<div
										style={{
											position: 'relative',
											height: '100%',
											width:
												selectedSongPartList.getLength() * measureWidth + PART_LABEL_WIDTH,
											top: 0,
											left: 0,
										}}
									>
										{selectedSongPartList.parts.map((part, i) => (
											<LibraryPart
												key={part.id}
												partIndex={i}
												part={part}
												height={partHeight}
												measureWidth={measureWidth}
												selectedSectionIds={selectedSectionsIds}
												selectSectionId={handleBoxSelect}
												deselectSectionId={handleBoxSelect} // toggles
												clearSelection={() => setSelectedSectionsIds([])}
												dragging={isDragging}
											/>
										))}
									</div>
								)}
							</div>
						</>
					)}
				</Paper>
			</Box>
		</div>
	);
});

export default Library;

export const LibraryConstants = {
	maxMeasureWidth: MAX_MEASURE_WIDTH,
	defaultZoom: DEFAULT_ZOOM,
	zoomIntervals: ZOOM_INTERVALS,
	zoomFocalPointEdgeSnapThreshold: ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD,
	minPartHeight: MIN_PART_HEIGHT,
	maxPartHeight: MAX_PART_HEIGHT,
	sectionLabelHeight: SECTION_LABEL_HEIGHT,
	partLabelWidth: PART_LABEL_WIDTH,
};
